/**
 * LiveOperation Page
 *
 * Real-time drone monitoring and infrastructure inspection page
 */

import { DroneFeedPanel } from '@/components/live-operation/DroneFeedPanel';
import MapViewer from '@/features/map/components/CesiumViewer';
import { MapControls } from '@/features/map/components/MapControls';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { Viewer as CesiumViewer } from 'cesium';
import { useWebSocket } from '@/hooks/useWebSocket';
import { LiveOperationData } from '@/types/websocket';
// import { useReplayData } from '@/features/replay/api';
// import { ReplayDataPoint } from '@/features/replay/types/replay.types';

export function LiveOperation() {
    const [viewer, setViewer] = useState<CesiumViewer | undefined>();
    const [selectedMapType, setSelectedMapType] = useState('1');
    const [liveData, setLiveData] = useState<LiveOperationData | null>(null);
    const [streamUrl, setStreamUrl] = useState<string | undefined>();
    // const [replayData, setReplayData] = useState<ReplayDataPoint[]>([]);

    const wsUrl = import.meta.env.VITE_WS_URL;
    const videoUrl = import.meta.env.VITE_URL_STREAM;
    // const replayWsUrl = import.meta.env.VITE_WS_LIVE_OPERATION_PANEL;

    // Fetch replay data
    // const { data: replayResponse } = useReplayData({ limit: 100 });

    // Initialize WebSocket connection for live operation data
    const { isConnected } = useWebSocket(wsUrl, {
        singleFetch: false,
        onData: (data: LiveOperationData) => {
            setLiveData(data);
            console.log('[LiveOperation] Received live data:', data);
        },
        onOpen: () => {
            console.log('[LiveOperation] Live WebSocket connected');
        },
        onClose: () => {
            console.log('[LiveOperation] Live WebSocket disconnected');
        },
        onError: (error) => {
            console.error('[LiveOperation] Live WebSocket error:', error);
        },
    });

        // Use WebSocket hook for replay data with single fetch mode
    // const { isConnected: isReplayConnected } = useWebSocket(replayWsUrl, {
    //     singleFetch: true,
    //     onData: (data: ReplayDataPoint | ReplayDataPoint[]) => {
    //         // Handle both array and single object responses
    //         const newData = Array.isArray(data) ? data : [data];
    //         setReplayData(newData);
    //         console.log('[Replay] Received data:', newData);
    //     },
    //     onOpen: () => {
    //         console.log('[Replay] WebSocket connected');
    //     },
    //     onClose: () => {
    //         console.log('[Replay] WebSocket disconnected');
    //     },
    //     onError: (error) => {
    //         console.error('[Replay] WebSocket error:', error);
    //     },
    // });

    // Log replay data when it changes
    // useEffect(() => {
    //     if (replayResponse?.data) {
    //         console.log('[Replay] Fetched replay data:', replayResponse.data);
    //         setReplayData(replayResponse.data);
    //     }
    // }, [replayResponse]);

    // Handle stream URL setup when WebSocket connects
    useEffect(() => {
        if (isConnected && videoUrl) {
            console.log('[LiveOperation] WebSocket connected, setting up stream URL:', videoUrl);
            setStreamUrl(videoUrl);
        }
    }, [isConnected, videoUrl]);

    const handleViewerReady = (cesiumViewer: CesiumViewer) => {
        setViewer(cesiumViewer);
    };

    const handleMapTypeChange = (mapType: string) => {
        setSelectedMapType(mapType);
    };

    return (
        <div className="absolute inset-0 flex flex-col pointer-events-none">
            {/* Left Panel */}
            <div
                className="absolute left-4 pointer-events-auto bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl overflow-hidden flex flex-col"
                style={{
                    right: 'calc(24rem + 1rem + 1rem)',
                    bottom: 'calc(25% + 1rem)',
                    top: 'calc(5rem)'
                }}
            >
                <div className="flex-1 relative bg-slate-800 rounded overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900">
                        <MapViewer onViewerReady={handleViewerReady} selectedMapType={selectedMapType} />
                        <MapControls viewer={viewer} selectedMapType={selectedMapType} onMapTypeChange={handleMapTypeChange} />
                    </div>
                </div>
            </div>

            {/* Right Panel - Drone Feed & Mission Details (Independent) */}
            <div
                className="absolute right-4 w-96 pointer-events-auto flex flex-col"
                style={{ bottom: '1rem', top: 'calc(5rem)' }}
            >
                <DroneFeedPanel url={streamUrl} />
            </div>

            {/* Bottom Panel - Telemetry (Unchanged) */}
            <div
                className="absolute bottom-4 left-4 h-[23%] pointer-events-auto"
                style={{ right: 'calc(24rem + 1rem + 1rem)' }}>
                <div className={cn(
                    'bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl',
                    'h-full overflow-hidden flex flex-col p-3'
                )}
                >
                    {/* Telemetry Grid - First Row */}
                    <div className="grid grid-cols-6 gap-3 h-[50%]">
                        {/* Call Sign */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Call Sign</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.callSign || 'AR-1 Armed Combat'}</p>
                        </div>

                        {/* Temperature */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Temperature</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.temperature || '32°C'}</p>
                        </div>

                        {/* Wind Speed */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Wind Speed</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.windSpeed || '8.5 m/s'}</p>
                        </div>

                        {/* Signal */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Signal</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.signal || '90 dBm'}</p>
                        </div>

                        {/* Battery - Spanning multiple columns */}
                        <div className="col-span-2 flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Battery</span>
                            </div>
                            <div className="flex-1 flex items-center justify-center">
                                <div className="flex items-center gap-2 w-full">
                                    <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                                        <div className="h-full bg-cyan-500" style={{ width: `${liveData?.battery || 50}%` }}></div>
                                    </div>
                                    <span className="text-xs font-medium text-white">{liveData?.battery || 50}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Second Row */}
                    <div className="grid grid-cols-6 gap-3 h-[50%] mt-3">
                        {/* Speed - Moved below Call Sign */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Speed</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.speed || '22 kmh'}</p>
                        </div>

                        {/* Altitude */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Altitude</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.altitude || '22 kmh'}</p>
                        </div>

                        {/* Heading */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Heading</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.heading || '22 kmh'}</p>
                        </div>

                        {/* Latitude */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Latitude</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.latitude || '-22 kmh'}</p>
                        </div>

                        {/* Longitude */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Longitude</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.longitude || '22 kmh'}</p>
                        </div>

                        {/* Satellites */}
                        <div className="flex flex-col justify-start bg-slate-700 p-3 rounded-md h-full">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-slate-600 rounded"></div>
                                <span className="text-xs text-slate-400">Satellites</span>
                            </div>
                            <p className="flex-1 flex items-center text-xs font-medium text-white">{liveData?.satellites || 14}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

